import { addSessionEvent } from '@/db/session-events';
import type { NewSessionEvent, SessionId, UserId } from '@/schemas';
import { createOperationId, type OperationQueue } from '../operation-queue';
import { createSystemEvent } from '../session-helpers';
import type { ImmerSetter } from '../session-store.types';

export type QueueDependencies = {
  operationQueue: OperationQueue;
  set: ImmerSetter;
  refreshSessionFromEvents: (sessionId: SessionId) => Promise<void>;
  updateUndoRedoAvailability: (sessionId: SessionId) => Promise<void>;
};

/**
 * Parameters for session event operations
 *
 * Note: While TypeScript can't perfectly infer generic types from object properties,
 * runtime validation via Zod schemas (in addSessionEvent) ensures payload correctness.
 * The payload should match the schema for the specified eventType.
 */
export type SessionEventOperationParams<TEventType extends NewSessionEvent['type']> =
  QueueDependencies & {
    sessionId: SessionId;
    operationType: string;
    eventType: TEventType;
    payload: Record<string, unknown>; // Validated at runtime via Zod schemas
    userId: UserId | undefined;
    dedupKey: string;
    priority?: number;
    refreshAfterSuccess?: boolean;
    optimisticUpdate: () => void;
    rollback: () => void;
  };

/**
 * Enqueue a session event operation with type-safe payload validation
 *
 * While TypeScript can't perfectly infer generic types from object properties,
 * runtime validation via Zod schemas (in addSessionEvent) ensures payload correctness.
 * The payload will be validated against the schema for the specified eventType.
 *
 * @param params - Operation parameters including eventType and payload
 * @throws ZodError if payload doesn't match the schema for eventType
 *
 * @example
 * ```ts
 * await enqueueSessionEventOperation({
 *   eventType: 'SessionNameChanged',
 *   payload: { name: 'New Name' }, // Must match SessionNameChanged payload schema
 *   // ...
 * });
 * ```
 */
export async function enqueueSessionEventOperation<
  TEventType extends NewSessionEvent['type'] = NewSessionEvent['type'],
>(
  params: Omit<SessionEventOperationParams<TEventType>, 'eventType'> & {
    eventType: TEventType;
    payload: Record<string, unknown>; // Runtime validation catches mismatches
  },
): Promise<void> {
  const {
    operationQueue,
    refreshSessionFromEvents,
    updateUndoRedoAvailability,
    sessionId,
    operationType,
    eventType,
    payload,
    userId,
    dedupKey,
    priority = 5,
    refreshAfterSuccess = false,
    optimisticUpdate,
    rollback,
  } = params;

  await operationQueue.enqueue<void>({
    id: createOperationId(),
    type: operationType,
    dedupKey,
    priority,
    optimisticUpdate,
    rollback: async () => {
      rollback();
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      await addSessionEvent(createSystemEvent(sessionId, eventType, payload, userId));
      await updateUndoRedoAvailability(sessionId);
      if (refreshAfterSuccess) {
        await refreshSessionFromEvents(sessionId);
      }
    },
  });
}
